import { AlertTriangle } from 'lucide-react'

export interface RisultatoControllo {
  supportato: boolean
  motivo?: string
}

/**
 * Verifica le API browser necessarie per la sessione vocale.
 *
 * Requisiti:
 *  - getUserMedia (microfono)
 *  - WebSocket
 *  - AudioContext (con sample rate 16kHz)
 *
 * Safari ha alcune particolarità: usa webkitAudioContext e non sempre
 * supporta MediaRecorder con opus — ma noi non usiamo MediaRecorder
 * (usiamo ScriptProcessor direttamente), quindi funziona anche su Safari.
 */
export function controllaSupportoBrowser(): RisultatoControllo {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices) {
    return { supportato: false, motivo: 'navigator.mediaDevices non disponibile' }
  }
  if (typeof navigator.mediaDevices.getUserMedia !== 'function') {
    return { supportato: false, motivo: 'getUserMedia non supportato' }
  }
  if (typeof WebSocket === 'undefined') {
    return { supportato: false, motivo: 'WebSocket non disponibile' }
  }
  const AudioCtor =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!AudioCtor) {
    return { supportato: false, motivo: 'AudioContext non supportato' }
  }
  return { supportato: true }
}

interface Props {
  risultato: RisultatoControllo
}

/**
 * Messaggio mostrato quando il browser non è compatibile.
 * Usato dentro SessioneVoce prima di chiedere il permesso microfono.
 */
export function BrowserNonSupportato({ risultato }: Props) {
  if (risultato.supportato) return null

  return (
    <div style={{
      padding: 24,
      background: 'var(--warning-soft)',
      color: 'var(--warning)',
      borderRadius: 'var(--radius-md)',
      display: 'flex',
      gap: 12,
      alignItems: 'flex-start',
    }}>
      <AlertTriangle size={20} />
      <div>
        <strong style={{ display: 'block', marginBottom: 4 }}>
          Browser non compatibile
        </strong>
        <div style={{ fontSize: 14, lineHeight: 1.5 }}>
          La modalità "Parla nel browser" richiede un browser recente.
          Usa <strong>Chrome</strong>, <strong>Firefox</strong> o <strong>Edge</strong>
          {' '}aggiornato all'ultima versione.
          {risultato.motivo && (
            <div style={{ marginTop: 6, fontSize: 12, opacity: 0.8 }}>
              Dettaglio tecnico: {risultato.motivo}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
